import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { TProcurementReturns } from 'utils/types';
import * as Yup from 'yup';

interface ReturnToSupplierDetailsFormProps {
  procurementReturnId: number;
}

export type ReturnToSupplierDetailsFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const ReturnToSupplierDetailsForm = forwardRef<
  ReturnToSupplierDetailsFormHandle,
  ReturnToSupplierDetailsFormProps
>(({ procurementReturnId }, ref) => {
  const [returnDetails, setReturnDetails] = useState({
    receivingAgentName: ''
  });
  const { selectedBranch } = useBranchContext();

  const returnToSupplierSchema = Yup.object().shape({
    receivingAgentName: Yup.string().required('Agent Name is required')
  });

  const handleSubmit = async () => {
    try {
      await returnToSupplierSchema.validate(returnDetails, {
        abortEarly: false
      });
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturns/${procurementReturnId}/returnToSupplier`,
        { receivingAgentName: returnDetails.receivingAgentName }
      );
      toast.success('Procurement returned to supplier', {
        autoClose: 2000,
        ...toastConfig
      });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      }
      return false;
    }
  };

  const handleFormChange = <K extends keyof TProcurementReturns>(
    field: K,
    value: TProcurementReturns[K]
  ) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <>
      <Form>
        <Row className="mb-3">
          <Form.Label>Recieving Agent</Form.Label>
          <Form.Control
            type="text"
            value={returnDetails.receivingAgentName}
            onChange={e =>
              handleFormChange('receivingAgentName', e.target.value)
            }
          />
        </Row>
      </Form>
    </>
  );
});

export default ReturnToSupplierDetailsForm;
